import { fn } from '@ember/helper';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import {
  PrismButton,
  PrismNumberInput,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import { t } from 'ember-intl';
import { eq, or } from 'ember-truth-helpers';
import type { RecommendationStrategy } from 'lumen/models';
import type {
  ComputedValueCondition,
  OperandCondition,
  UserEditableCondition,
} from 'lumen/models/recommendation-strategy';
import {
  PRICE_RANGE_STRATEGY_CONDITION_OPERATORS,
  PRICE_RANGE_STRATEGY_CONDITION_TRANSLATIONS,
  PRICE_RANGE_STRATEGY_CONDITION_TYPES,
} from 'lumen/utils/constants/pricing-assistant-constants';

type StrategyTableColumn = CustomEmberTableColumn & {
  isEditing: boolean;
  strategy: RecommendationStrategy;
};

type StrategyTableRow = CustomEmberTableRow & {
  addCondition?: () => void;
  deleteCondition?: () => void;
  label: string;
  onConditionOperatorChange?: (operation: Option) => void;
  onConditionTypeChange?: (type: Option) => void;
  onConditionValueChange?: (id: string, value: number | null) => void;
  setting: string;
};

interface SettingsRecommendationsPriceRangeTdStrategyConditionSignature {
  Args: {
    column: StrategyTableColumn;
    row: StrategyTableRow;
    value: string;
  };
}

export default class SettingsRecommendationsPriceRangeTdStrategyCondition extends Component<SettingsRecommendationsPriceRangeTdStrategyConditionSignature> {
  @service declare access: Services['access'];
  @service declare intl: Services['intl'];

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get conditionOperatorOptions() {
    if (this.conditionType) {
      if (
        this.conditionType === PRICE_RANGE_STRATEGY_CONDITION_TYPES.OTB ||
        this.conditionType === PRICE_RANGE_STRATEGY_CONDITION_TYPES.OTB_FORECAST
      ) {
        return [
          {
            id: PRICE_RANGE_STRATEGY_CONDITION_OPERATORS.GT,
            label: this.intl.t(
              'pricing_assistant.recommendations.price_range.gt',
            ),
          },
          {
            id: PRICE_RANGE_STRATEGY_CONDITION_OPERATORS.LT,
            label: this.intl.t(
              'pricing_assistant.recommendations.price_range.lt',
            ),
          },
        ];
      }

      if (
        this.conditionType === PRICE_RANGE_STRATEGY_CONDITION_TYPES.LEADTIME
      ) {
        return [
          {
            id: PRICE_RANGE_STRATEGY_CONDITION_OPERATORS.GTE,
            label: this.intl.t(
              'pricing_assistant.recommendations.price_range.gte',
            ),
          },
          {
            id: PRICE_RANGE_STRATEGY_CONDITION_OPERATORS.LTE,
            label: this.intl.t(
              'pricing_assistant.recommendations.price_range.lte',
            ),
          },
        ];
      }
    }

    return [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get conditions() {
    return this.args.column.strategy.conditionalStrategies?.[0]
      ?.conditions as OperandCondition & {
      first?: ComputedValueCondition;
      second?: UserEditableCondition;
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get conditionType() {
    if (this.conditions && this.conditions.first) {
      return this.conditions.first.name;
    }

    return null;
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get conditionTypeOptions() {
    return [
      {
        id: PRICE_RANGE_STRATEGY_CONDITION_TYPES.OTB,
        label: this.intl.t(
          'pricing_assistant.recommendations.price_range.condition.otb',
        ),
      },
      ...(this.access.hasOTBForecastView
        ? [
            {
              id: PRICE_RANGE_STRATEGY_CONDITION_TYPES.OTB_FORECAST,
              label: this.intl.t(
                'pricing_assistant.recommendations.price_range.condition.otb_forecast',
              ),
            },
          ]
        : []),
      {
        id: PRICE_RANGE_STRATEGY_CONDITION_TYPES.LEADTIME,
        label: this.intl.t(
          'pricing_assistant.recommendations.price_range.condition.leadtime',
        ),
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get conditionValue() {
    const number = this.conditions?.second?.value;

    if (number !== null) {
      if (
        this.conditionType === PRICE_RANGE_STRATEGY_CONDITION_TYPES.OTB ||
        this.conditionType === PRICE_RANGE_STRATEGY_CONDITION_TYPES.OTB_FORECAST
      ) {
        return number * 100;
      }

      return number;
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedCondition() {
    if (
      this.conditions &&
      this.conditions.operation &&
      Object.values(PRICE_RANGE_STRATEGY_CONDITION_OPERATORS).includes(
        this.conditions.operation,
      )
    ) {
      const translationKey =
        PRICE_RANGE_STRATEGY_CONDITION_TRANSLATIONS[
          this.conditions
            .operation as keyof typeof PRICE_RANGE_STRATEGY_CONDITION_OPERATORS
        ];

      if (translationKey && this.selectedConditionType) {
        return this.intl.t(translationKey, {
          type: this.selectedConditionType.label,
          value: this.formattedConditionValue,
        });
      }
    }

    return '';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedConditionValue() {
    const number = this.conditions?.second?.value;

    if (number) {
      if (
        this.conditionType === PRICE_RANGE_STRATEGY_CONDITION_TYPES.OTB ||
        this.conditionType === PRICE_RANGE_STRATEGY_CONDITION_TYPES.OTB_FORECAST
      ) {
        return this.intl.formatNumber(number, {
          style: 'percent',
        });
      }

      return this.intl.formatNumber(number);
    }

    return '';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedConditionOperator() {
    return this.conditionOperatorOptions.find(
      (option) => option.id === this.conditions?.operation,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedConditionOperatorOption() {
    return this.conditionOperatorOptions.find(
      (option) => option.id === this.conditions.operation,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedConditionType() {
    return this.conditionTypeOptions.find(
      (option) => option.id === this.conditions?.first?.name,
    );
  }

  <template>
    <div class="strategy-table-first-column">
      {{#if (eq @row.setting "condition")}}
        <div class="condition-cell flex items-center">
          {{#if @column.strategy.conditionalStrategies}}
            {{#if @column.isEditing}}
              <div class="flex flex-row items-center">
                <div class="flex items-center gap-200">
                  <p>{{t "pricing_assistant.if"}}</p>
                  {{#if @row.onConditionTypeChange}}
                    <PrismSelectInput
                      @isSmall={{true}}
                      @onChange={{@row.onConditionTypeChange}}
                      @options={{this.conditionTypeOptions}}
                      @placeholder={{t
                        "pricing_assistant.recommendations.price_range.condition_type.placeholder"
                      }}
                      @value={{this.selectedConditionType}}
                    />
                  {{/if}}
                  {{#if this.conditionType}}
                    {{#if @row.onConditionOperatorChange}}
                      <PrismSelectInput
                        @isSmall={{true}}
                        @onChange={{@row.onConditionOperatorChange}}
                        @options={{this.conditionOperatorOptions}}
                        @placeholder={{t
                          "pricing_assistant.recommendations.price_range.condition_operator.placeholder"
                        }}
                        @value={{this.selectedConditionOperator}}
                      />
                    {{/if}}
                    {{#if this.selectedConditionOperator}}
                      {{#if @row.onConditionValueChange}}
                        <div class="condition-cell__input flex items-center">
                          <PrismNumberInput
                            @inlineLabel={{if
                              (or
                                (eq
                                  this.conditionType
                                  PRICE_RANGE_STRATEGY_CONDITION_TYPES.OTB
                                )
                                (eq
                                  this.conditionType
                                  PRICE_RANGE_STRATEGY_CONDITION_TYPES.OTB_FORECAST
                                )
                              )
                              "%"
                            }}
                            @min={{0}}
                            @onValueChange={{fn
                              @row.onConditionValueChange
                              this.conditionType
                            }}
                            @placeholder={{t "demand.value"}}
                            @removePadding={{true}}
                            @value={{this.conditionValue}}
                          />
                        </div>
                      {{/if}}
                    {{else}}
                      ...
                    {{/if}}
                  {{else}}
                    ...
                  {{/if}}
                </div>
                <div class="condition-cell__delete-button mr-200">
                  <PrismButton
                    @buttonType="ghost"
                    @icon="bin"
                    @onClick={{@row.deleteCondition}}
                    @size="small"
                  />
                </div>
              </div>
            {{else}}
              {{this.formattedCondition}}
            {{/if}}
          {{else}}
            <div class="condition-cell__add-button">
              <PrismButton
                @buttonType="secondary"
                @iconBefore="plus"
                @label={{t
                  "pricing_assistant.recommendations.price_range.add_condition"
                }}
                @onClick={{@row.addCondition}}
                @size="small"
              />
            </div>
          {{/if}}
        </div>
      {{else}}
        <span class="text-200--bold">{{@row.label}}</span>
      {{/if}}
    </div>
  </template>
}
